import _ from 'lodash'
import SpecialRules from './SpecialRules'
import { StringKeyed } from '../types/util'

const formatNumber = (a?: number) => a == null ? 'NA' : Math.abs(a) < 1 ? a.toPrecision(3) : a.toFixed(3)

let currentConfiguration
type Convertor = (input: number) => number
export interface Settings {
	analyte?: string
	isChange?: boolean
}
const UNIT_SPLIT = /([^\/]*)\/?(.*)/
const asFunctions: StringKeyed<Convertor> = {}
const UnitConvert = {
	unitFixes: {
		week: 'wk',
		w: 'wk',
		month: 'mo',
		months: 'mo',
		days: 'd',
		day: 'd',
		dy: 'd',
		year: 'yr',
		years: 'yr',
		second: 's',
		minute: 'm',
		hour: 'h',
		gram: 'g',
	},
	units: {
		global : {},
	},
	analytes: [],
	indexConversions(conversions) {
		for (const conversion of conversions || []) {
			const analyte = conversion.RootTerm && conversion.RootTerm.Name
			if (analyte && !this.analytes.includes(analyte)){
				this.analytes.push(analyte)
			}
			const globalSet = this.units.global
			const analyteSet = analyte ? (this.units[analyte] || (this.units[analyte] = Object.create(this.units.global))) : globalSet

			const sourceName = conversion.SourceUnit.Name || conversion.SourceUnit
			const globalSourceSet = globalSet[sourceName] || (globalSet[sourceName] = {})
			const sourceSet = analyte ?
				(analyteSet.hasOwnProperty(sourceName) ?
					analyteSet[sourceName] :
					(analyteSet[sourceName] = Object.create(globalSourceSet))) :
				globalSourceSet
			const destinationName = conversion.DestinationUnit.Name || conversion.DestinationUnit
			if (destinationName != sourceName) {
				sourceSet[destinationName] = {
					isDefault: conversion.IsDefault,
					isSI: conversion.IsDestinationSi,
					multiplier: conversion.Multiplier,
				}
				if (conversion.conversion) {
					sourceSet[destinationName].conversion = conversion.conversion
				}
			}
		}
	},
	convertValue(fromValue: number, fromUnit: string /*RangePart.Unit*/, toUnit: string, settings?: Settings) {
		let analyte
		if (settings) {
			analyte = settings.analyte
		}
		const conversion = this.getConversion(fromUnit, toUnit, analyte)
		if (typeof conversion === 'string') {
			const isChange = settings && settings.isChange
			let converter = asFunctions[conversion + (isChange ? 'change' : '')]
			if (!converter) {
				let asJS = (isChange ? conversion.replace(/\s*[+-]\s*[0-9\.-]+\s*/g, '') : conversion) // remove any constant if it is a change
				asJS = 'return (' + asJS.replace(/x/g, '*').replace(/log/, 'Math.log10').replace(/value\^(\d+)/, 'Math.pow($1, value)') + ')'
				asFunctions[conversion + (isChange ? 'change' : '')] = converter = new Function('value', asJS) as Convertor
			}
			const toValue = converter(fromValue)
			return Math.abs(toValue) > 0.00001 ? Math.round(toValue * 1000000000000) / 1000000000000 : toValue
		} else if (isNaN(conversion)) {
			return fromValue
		} else {
			const toValue = conversion * fromValue
			// multiply, then eliminate decimal errors
			return Math.abs(toValue) > 0.00001 ? Math.round((conversion * fromValue) * 1000000000000) / 1000000000000 : toValue
		}
	},
	getConversion(fromUnit, toUnit, analyte?: string) {
		// Do unit conversion
		// normalize to a string first
		if (analyte && this.units[analyte] === undefined) {
			analyte = analyte.toLowerCase()
			let found
			for (let possibleAnalyte of this.analytes) {
				if (analyte.indexOf(possibleAnalyte.toLowerCase()) > -1) {
					this.units[analyte] = this.units[possibleAnalyte] // equate these so they are cached
					found = true
					break
				}
			}
			if (!found)
				this.units[analyte] = null // save this as null so we don't keep searching this over and over
		}
		fromUnit = this.unitToString(fromUnit)
		toUnit = this.unitToString(toUnit)
		fromUnit = fromUnit.replace(/\(.*\)/g, '')
		toUnit = toUnit.replace(/\(.*\)/g, '')
		if (this.unitFixes[fromUnit]) {
			fromUnit = this.unitFixes[fromUnit]
		}
		if (this.unitFixes[toUnit]) {
			toUnit = this.unitFixes[toUnit]
		}
		if (fromUnit === toUnit) { // equal, no conversion necessary
			return 1
		} else {
			// first look for a direct conversion
			let conversion
			const units = this.units[analyte] || this.units.global
			if (fromUnit === toUnit) {
				return 1
			}
			else {
				if (units && units[fromUnit] && units[fromUnit][toUnit]) {
					const conversion = units[fromUnit][toUnit]
					return conversion.multiplier || conversion.conversion
				}
				if (units && units[toUnit] && units[toUnit][fromUnit]) {
					// try the reverse conversion next
					const conversion = units[toUnit][fromUnit]
					return 1 / conversion.multiplier
				}
			}

			if (!conversion && units[fromUnit] && units[toUnit]) {
				// if one stage conversion didn't work, try to 2 stage conversion, converting to a primary first
				const firstConversions = units[fromUnit]
				for (const intermediate in firstConversions) {
					let secondConversion = ((units[intermediate] && units[intermediate][toUnit]) ||
						(units[toUnit] && units[toUnit][intermediate])) &&
						this.getConversion(intermediate, toUnit, analyte)
					if (secondConversion) {
						let firstConversion = firstConversions[intermediate]
						firstConversion = firstConversion.multiplier || firstConversion.conversion
						if (typeof firstConversion === 'string' || typeof secondConversion === 'string') {
							if (typeof firstConversion === 'number') {
								firstConversion = 'value x ' + firstConversion
							}
							if (typeof secondConversion === 'number') {
								secondConversion = 'value x ' + secondConversion
							}
							// basically do a nested equation
							return secondConversion.replace(/value/, '(' + firstConversion + ')')
						}
						return firstConversion * secondConversion
					}
				}
			}

			const fromSelfMultiplyingUnit = fromUnit.match(/per (\d+) (.+)/)
			const toSelfMultiplyingUnit = toUnit.match(/per (\d+) (.+)/)
			if (fromSelfMultiplyingUnit && toSelfMultiplyingUnit) {
				// we have self-defined unit conversion
				const [, fromMultiplier, fromMultipliedUnit] = fromSelfMultiplyingUnit
				const [, toMultiplier, toMultipliedUnit] = toSelfMultiplyingUnit
				return toMultiplier / fromMultiplier / this.getConversion(fromMultipliedUnit, toMultipliedUnit, analyte)
			}

			const fromMultiPart = fromUnit.match(/(\w+)[\s-]+(.+)/)
			const toMultiPart = toUnit.match(/(\w+)[\s-]+(.+)/)
			if (fromMultiPart && toMultiPart) {
				const [, fromFirst, fromNext] = fromMultiPart
				const [, toFirst, toNext] = toMultiPart

				return this.getConversion(fromFirst, toFirst) * this.getConversion(fromNext, toNext, analyte)
			}

			// split on / to see if it is multi-part unit
			const [, fromFirst, fromNext] = fromUnit.match(UNIT_SPLIT)
			const [, toFirst, toNext] = toUnit.match(UNIT_SPLIT)
			if (!fromFirst || !toFirst) {
				// if either unit is missing we just consider it equal
				return NaN
			}
			if (fromNext || toNext) {
				// if there is a next part, find the conversion on it and divide
				return this.getConversion(fromFirst, toFirst) / this.getConversion(fromNext, toNext, analyte)
			}
			// fallback to 1 if we have no conversion
			return NaN
		}
	},

	checkBadUnit(unit) {
		// sometimes units have numbers in them, and we have to correct that
		let number
		const parts = unit != null ? unit.match(/([\d\.-]+)\s*(.+?)s?\s*$/) : undefined
		if (parts) {
			number = parseFloat(parts[0])
			unit = parts[1]
			if (this.unitFixes[unit]) {
				unit = this.unitFixes[unit]
			}
			return [number, unit]
		}
	},

	getPreferredUnitValue(unit, analyte) {
		return this.getDefaultUnitValue(unit, analyte) ||
		{
			unit,
			multiplier: 1,
		}
	},

	getDefaultUnitValue(unit, analyte) {
		const units = this.units[analyte] || this.units.global
		if (!units) {
			return
		}
		for (const destinationUnit in units[unit]) {
			const destination = units[unit][destinationUnit]
			const isSI = (currentConfiguration && currentConfiguration.isSI != null) ?
				currentConfiguration.isSI :
				/*!!app.user.IsSI*/true
			if (destination.isDefault && destination.isSI === isSI) {
				return {
					unit: destinationUnit,
					multiplier: destination.multiplier,
				}
			}
		}
	},

	/**
	@name standardizeUnits
	@description
	Normalizes units into strings, checking for known unit types with
	a default conversion.

	Note: mutates objects contained in original input array. This might need
	to be refactored if it causes any issues.

	@param {Array.<{unit: Object|String, number: Number|String}>} values

	@returns {Array.<{unit: String, number: Number|String}>}
	**/

	standardizeUnits(values: unitvalue[]) {
		// convert all the units to strings, and check to see if we have a default conversion
		return values.map((value) => {
			return Object.assign(value, {
				unit: this.unitToString(value.unit),
				number: value.number,
			})
		})
	},

	getPreferredUnit(values: unitvalue[], analyte?) {
		if (values.length === 0) {
			return
		}
		// convert all the units to strings, and check to see if we have a default conversion
		const standardizedUnits = this.standardizeUnits(values, analyte).filter((point) => point.unit)

		let defaultUnit
		// if we don't have a default unit, we use what is most common in this group
		const mostFrequent = _.maxBy(_.map(_.countBy(standardizedUnits, 'unit'), (count, unit) => {
			defaultUnit = defaultUnit || this.getDefaultUnitValue(unit, analyte)
			return {count, unit}
		}), 'count')
		return (defaultUnit || mostFrequent || {unit: ''}).unit
	},

	/**
	@name normalizeValues
	@description
	Normalizes an array of objects with convertible units (e.g., lb -> kg)
	Any values that cannot be converted are thrown out.
	**/
	normalizeValues(values: unitvalue[], settings?: Settings): NormalizeResult {
		if (values.length === 0) {
			return values
		}
		settings = settings || {}
		const analyte = settings.analyte
		// convert all the units to strings, and check to see if we have a default conversion
		const standardizedUnits = this.standardizeUnits(values, analyte)
		const toUnit = this.getPreferredUnit(standardizedUnits, analyte)

		const results = [] as NormalizeResult
		let hasUnconvertableValues = false
		standardizedUnits.forEach((value) => {
			const number = value.number
			let newNumber
			let converted
			if (value.unit == toUnit) {
				// no conversion if the units are the same
				newNumber = number
			} else {
				const conversion = UnitConvert.getConversion(value.unit, toUnit, analyte)
				if (typeof conversion === 'string') {
					// custom equation
					newNumber = UnitConvert.convertValue(number, value.unit, toUnit, settings)
					const isChange = settings && settings.isChange
					const equation = (isChange ? conversion.replace(/\s*[+-]\s*[0-9\.-]+\s*/g, '') : conversion) // remove any constant if it is a change
					converted = equation.replace(/value/g, number + ' ' + value.unit)
				} else if (number === 0) {
					// 0 doesn't require conversion with proportional transform
					newNumber = 0
				} else {
					// proportional conversion
					newNumber = UnitConvert.convertValue(number, value.unit, toUnit, settings)
					converted = number + ' ' + value.unit + ' x ' + conversion
				}
			}
			if (Number.isFinite(newNumber)) {
				results.push(Object.assign(value, {
					unit: toUnit,
					number: newNumber,
					conversion: converted,
				}))
			} else {
				hasUnconvertableValues = true
			}
		})
		results.hasUnconvertableValues = hasUnconvertableValues
		return results
	},

	getTransformation(value, unit, toUnit, settings?) {
		const conversion = UnitConvert.getConversion(unit, toUnit, settings && settings.analyte)
		const newNumber = UnitConvert.convertValue(value, unit, toUnit, settings)
		if (typeof conversion === 'string') {
			const isChange = settings && settings.isChange
			const equation = (isChange ? conversion.replace(/\s*[+-]\s*[0-9\.-]+\s*/g, '') : conversion) // remove any constant if it is a change
			return equation.replace(/value/g, value + ' ' + unit) + ' = ' + newNumber + ' ' + toUnit
		} else if (isNaN(conversion)) {
			return
		} else {
			return value + ' ' + unit + ' x ' + conversion + ' = ' + formatNumber(newNumber) + ' ' + toUnit
		}
	},

	unitToString(unit): string {
		if (unit && unit.Unit) {
			if (unit.Per) {
				return unit.Unit + '/' + this.unitToString(unit.Per)
			} else {
				return unit.Unit
			}
		} else {
			return unit || ''
		}
	},

	forConfiguration<T>(workflowConfiguration, executor: (...args: any[]) => T) {
		currentConfiguration = workflowConfiguration
		try {
			return executor()
		} finally {
			currentConfiguration = null
		}
	}
}

export interface unitvalue {
	number: number | string
	unit: UnitPer | string
}

type NormalizeResult = unitvalue[] & { hasUnconvertableValues?: boolean }

interface UnitPer {
	Unit: string
	Per?: UnitPer
}

UnitConvert.indexConversions(SpecialRules)
global.UnitConvert = UnitConvert
export default UnitConvert